import React, { useMemo } from 'react';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import type { ProposalBundle, ProposalItem } from '../api/proposalBundles';
import { formatRouteDate } from '../utils/routeInfoFormatter';
import { formatProposalPrice } from '../utils/priceFormatter';

interface Props {
  bundles: ProposalBundle[];
  includeTax?: boolean;
}

const getCategoryLabel = (category: ProposalBundle['category']): string => {
  switch (category) {
    case 'outward':
      return '行き';
    case 'homeward':
      return '帰り';
    case 'transport':
      return '交通';
    case 'hotel':
      return 'ホテル';
    case 'package':
      return 'パッケージ';
    default:
      return category;
  }
};

const getStateLabel = (state: ProposalBundle['state']): { text: string; color: string } => {
  switch (state) {
    case 'suggesting':
      return { text: '提案中', color: '#d4a60d' };
    case 'selected':
      return { text: '選択済み', color: '#4a9464' };
    case 'rejected':
      return { text: '予約しない', color: '#888888' };
    default:
      return { text: state, color: '#666' };
  }
};

const getSelectedItem = (bundle: ProposalBundle): ProposalItem | null => {
  return bundle.items.find(item => item.selected) || null;
};

const LatestProposals = ({ bundles, includeTax = false }: Props) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);

  if (bundles.length === 0) {
    return <p style={styles.placeholder}>提案内容がまだありません</p>;
  }

  return (
    <div style={styles.container}>
      {bundles.map(bundle => {
        const stateInfo = getStateLabel(bundle.state);
        const dateStr = formatRouteDate(bundle.departureDate);
        const routeInfo = bundle.origin && bundle.destination ? `${bundle.origin} → ${bundle.destination}` : null;
        const selectedItem = getSelectedItem(bundle);

        return (
          <div key={bundle.id} style={styles.bundleCard}>
            <div style={styles.bundleHeader}>
              <span style={styles.categoryLabel}>{getCategoryLabel(bundle.category)}</span>
              <span style={{ ...styles.stateLabel, color: stateInfo.color }}>{stateInfo.text}</span>
            </div>
            {(routeInfo || dateStr) && (
              <div style={styles.routeInfo}>
                {routeInfo && <span>{routeInfo}</span>}
                {dateStr && <span style={styles.dateText}>（{dateStr}）</span>}
              </div>
            )}
            {selectedItem ? (
              <div style={styles.selectedItemInfo}>
                <div style={styles.selectedItemTitle}>
                  {selectedItem.flightName || selectedItem.title || '-'}
                  {selectedItem.seatClass && <span style={styles.seatClass}>（{selectedItem.seatClass}）</span>}
                </div>
                {selectedItem.departureTime && selectedItem.arrivalTime && (
                  <div style={styles.selectedItemTime}>
                    {selectedItem.departureTime} - {selectedItem.arrivalTime}
                  </div>
                )}
                <div style={styles.selectedItemPrice}>
                  {formatProposalPrice({
                    price: selectedItem.price,
                    priceWithTax: selectedItem.priceWithTax,
                    includeTax
                  })}
                </div>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

const getStyles = (theme: AITTheme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
    marginTop: '12px'
  },
  placeholder: {
    textAlign: 'center' as const,
    color: theme.grayTextColor,
    margin: '16px 0 0',
    fontSize: '13px'
  },
  bundleCard: {
    padding: '10px 12px',
    backgroundColor: theme.contentBgColor,
    borderRadius: '6px',
    border: `1px solid ${theme.grayBorderColor}`
  },
  bundleHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginBottom: '4px'
  },
  categoryLabel: {
    fontSize: '13px',
    fontWeight: 600 as const,
    flex: 1
  },
  stateLabel: {
    fontSize: '11px',
    fontWeight: 500 as const
  },
  routeInfo: {
    fontSize: '12px',
    color: theme.grayTextColor,
    marginBottom: '4px'
  },
  dateText: {
    color: theme.grayTextColor
  },
  itemCount: {
    fontSize: '11px',
    color: theme.grayTextColor
  },
  selectedItemInfo: {
    marginTop: '6px',
    padding: '8px',
    backgroundColor: '#e8f5e9',
    borderRadius: '4px'
  },
  selectedItemTitle: {
    fontSize: '13px',
    fontWeight: 600 as const,
    color: theme.textColor
  },
  seatClass: {
    fontSize: '12px',
    fontWeight: 400 as const
  },
  selectedItemTime: {
    fontSize: '12px',
    color: theme.grayTextColor,
    marginTop: '2px'
  },
  selectedItemPrice: {
    fontSize: '13px',
    fontWeight: 600 as const,
    color: theme.linkColor,
    marginTop: '4px'
  }
});

export default LatestProposals;
